import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { CircularProgress } from "@mui/material";
import api from "@/utils/connector/ApiConnector";
import { ForgotPasswordModal } from "../../components/ForgotPasswordModal/ForgotPasswordModal";
import { isAuthenticated } from "@/utils/auth/isAuthenticated";
import logo from "@/assets/cowonderlogo.png";

const CHART_LINE = "M0,88 C30,80 50,72 80,62 C110,52 130,58 160,44 C190,30 210,38 240,24 C270,10 290,18 320,12 C350,6 370,10 400,8";
const CHART_AREA = `${CHART_LINE} L400,120 L0,120 Z`;
const CHART_LEN  = 820;

const kpis = [
  { label: 'Receita', value: 'R$42k', delta: '+18%', color: '#4ade80' },
  { label: 'Contratos', value: '318', delta: '+7', color: '#cb88f9' },
  { label: 'Ocupação', value: '76%', delta: '+4%', color: '#60a5fa' },
];


function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("harenaCowonder") || localStorage.getItem("harenaCowonderAuth");
    if (stored && isAuthenticated(stored)) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      storeEmail()
      const response = await api.post("/auth/login", { email, password }) as {
        access_token: string;
        name: string;
        coworkingId: string;
        role: 'OWNER' | 'OPERATOR';
      };
      const userDataStorage = {
        email,
        auth: response.access_token,
        name: response.name,
        coworkingId: response.coworkingId,
        role: response.role,
      };
      console.log(userDataStorage)
      const serializedUserData = JSON.stringify(userDataStorage);
      localStorage.setItem("harenaCowonder", serializedUserData);
      localStorage.setItem("harenaCowonderAuth", serializedUserData);

      navigate("/dashboard")
      window.location.reload();
    } catch (error) {
      console.error("Erro ao fazer login:", error);
    } finally {
      setLoading(false);
    }
  };

  function storeEmail() {
    if (rememberMe) {
      localStorage.setItem("email", email);
    } else {
      localStorage.removeItem("email");
    }
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const [chartDone, setChartDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setChartDone(true), 1800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="flex h-screen w-screen overflow-hidden">

      {/* ── Painel Esquerdo — Dashboard Mockup ── */}
      <div
        className="hidden lg:flex flex-col w-[58%] relative overflow-hidden"
        style={{ background: '#06000f' }}
      >
        {/* Grid de pontos */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(rgba(203,136,249,0.12) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        {/* Blobs */}
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(121,28,184,0.35) 0%, transparent 65%)', filter: 'blur(80px)' }} />
        <div className="absolute -bottom-40 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(73,15,112,0.4) 0%, transparent 65%)', filter: 'blur(70px)' }} />

        {/* Topo: logo + badge */}
        <motion.div
          className="relative z-10 flex items-center justify-between px-10 pt-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        >
          <img src={logo} alt="Cowonder" className="h-7" />
          <div className="flex items-center gap-2 text-xs font-bold text-green-400 bg-green-400/10 border border-green-400/20 px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
            Ao vivo
          </div>
        </motion.div>

        {/* Dashboard window */}
        <motion.div
          className="relative z-10 flex-1 mx-8 my-6 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: 'rgba(12,4,26,0.9)', border: '1px solid rgba(203,136,249,0.12)', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
        >
          {/* Chrome */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
            <span className="ml-3 text-white/25 text-[10px] font-mono">cowonder.app/dashboard</span>
          </div>

          <div className="flex-1 p-5 flex flex-col gap-4 overflow-hidden">
            {/* KPI row */}
            <div className="grid grid-cols-3 gap-3">
              {kpis.map((k, i) => (
                <motion.div
                  key={k.label}
                  className="rounded-xl p-3.5"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 + i * 0.1 }}
                >
                  <p className="text-white/35 text-[9px] uppercase tracking-wider mb-1">{k.label}</p>
                  <p className="text-white font-bold text-xl leading-none">{k.value}</p>
                  <p className="text-[10px] font-semibold mt-1.5" style={{ color: k.color }}>{k.delta}</p>
                </motion.div>
              ))}
            </div>

            {/* Chart */}
            <div className="rounded-xl flex-1 min-h-0 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <p className="text-white/60 text-[11px] font-semibold">Receita Mensal</p>
                <span className="text-[#cb88f9] text-[9px] font-bold bg-[#cb88f9]/10 px-2 py-0.5 rounded-full">+72% semestre</span>
              </div>
              <div className="flex-1 px-2 pb-2">
                <svg viewBox="0 0 400 120" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#cb88f9" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#cb88f9" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="ls" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#490f70" />
                      <stop offset="100%" stopColor="#cb88f9" />
                    </linearGradient>
                  </defs>
                  {/* Area fill */}
                  <motion.path d={CHART_AREA} fill="url(#lg)"
                    initial={{ opacity: 0 }} animate={{ opacity: chartDone ? 1 : 0 }}
                    transition={{ duration: 0.6 }}
                  />
                  {/* Line stroke — draws in */}
                  <motion.path
                    d={CHART_LINE}
                    fill="none" stroke="url(#ls)" strokeWidth="2.5" strokeLinecap="round"
                    strokeDasharray={CHART_LEN}
                    initial={{ strokeDashoffset: CHART_LEN }}
                    animate={{ strokeDashoffset: 0 }}
                    transition={{ duration: 1.6, delay: 0.5, ease: 'easeInOut' }}
                  />
                  {/* Glowing dot at end */}
                  <motion.circle cx="400" cy="8" r="4" fill="#cb88f9"
                    initial={{ opacity: 0 }} animate={{ opacity: chartDone ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.circle cx="400" cy="8" r="8" fill="#cb88f9" fillOpacity="0.2"
                    initial={{ scale: 0 }} animate={{ scale: chartDone ? [1, 1.6, 1] : 0 }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: 1.8 }}
                  />
                </svg>
              </div>
            </div>


          </div>
        </motion.div>

        {/* Tagline rodapé */}
        <motion.p
          className="relative z-10 text-center text-white/20 text-[11px] pb-5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.8 }}
        >
          Tudo que você precisa, em tempo real.
        </motion.p>
      </div>

      {/* ── Painel Direito — Formulário ── */}
      <div className="flex-1 flex flex-col relative" style={{ background: '#faf8ff' }}>
        {/* Tarja gradiente topo */}
        <div className="absolute top-0 inset-x-0 h-1"
          style={{ background: 'linear-gradient(90deg, #490f70, #cb88f9, #791cb8)' }} />

        {/* Botão voltar */}
        <motion.button
          className="absolute top-6 left-6 flex items-center gap-1.5 text-[#490f70] text-xs font-semibold hover:underline z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          onClick={() => navigate('/')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Voltar
        </motion.button>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-8 py-16">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            {/* Mobile logo */}
            <div className="flex lg:hidden justify-center mb-8">
              <img src={logo} alt="Cowonder" className="h-8" />
            </div>

            <h2 className="font-extrabold text-3xl text-[#1a0533] mb-1">Bem-vindo de volta</h2>
            <p className="text-gray-400 text-sm mb-10">Acesse seu painel de gestão</p>

            <form
              onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
              className="flex flex-col gap-5"
            >
              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="text-[#490f70] text-xs font-bold uppercase tracking-wider">E-mail</label>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-4 rounded-xl border-[1.5px] border-[#e8dff7] bg-white text-[#1a0533] text-sm outline-none transition-all placeholder:text-gray-300 focus:border-[#791cb8] focus:shadow-[0_0_0_4px_rgba(121,28,184,0.08)]"
                />
              </div>

              {/* Senha */}
              <div className="flex flex-col gap-2">
                <label className="text-[#490f70] text-xs font-bold uppercase tracking-wider">Senha</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 pr-12 rounded-xl border-[1.5px] border-[#e8dff7] bg-white text-[#1a0533] text-sm outline-none transition-all placeholder:text-gray-300 focus:border-[#791cb8] focus:shadow-[0_0_0_4px_rgba(121,28,184,0.08)]"
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#791cb8] transition-colors"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <Visibility sx={{ fontSize: 20 }} /> : <VisibilityOff sx={{ fontSize: 20 }} />}
                  </button>
                </div>
              </div>

              {/* Lembrar + Esqueci */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <div
                    className="relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0"
                    style={{ background: rememberMe ? '#490f70' : '#d1d5db' }}
                    onClick={() => setRememberMe(!rememberMe)}
                  >
                    <div
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                      style={{ transform: rememberMe ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </div>
                  <span className="text-gray-500 text-xs">Lembrar de mim</span>
                </label>
                <button
                  type="button"
                  className="text-[#791cb8] text-xs font-semibold hover:underline"
                  onClick={() => setForgotOpen(true)}
                >
                  Esqueci minha senha
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-2 py-4 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 transition-all"
                style={{
                  background: 'linear-gradient(135deg, #490f70 0%, #791cb8 100%)',
                  boxShadow: '0 8px 28px rgba(73,15,112,0.35)',
                }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Entrar no painel'}
              </button>
            </form>

            {/* Divisor */}
            <div className="flex items-center gap-3 my-8">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-gray-400 text-xs">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Contato */}
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-3">Ainda não tem uma conta?</p>
              <button
                type="button"
                className="w-full py-3.5 rounded-xl border-[1.5px] border-[#490f70] text-[#490f70] font-bold text-sm hover:bg-[#490f70] hover:text-white active:scale-[0.98] transition-all duration-200"
                onClick={() => window.open('https://wa.me/5581981159904', '_blank')}
              >
                Fale conosco no WhatsApp
              </button>
            </div>
          </motion.div>
        </div>

        {/* Rodapé */}
        <p className="text-center text-gray-300 text-xs pb-6">
          © 2026 Cowonder. Todos os direitos reservados.
        </p>
      </div>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}

export default Login;
